import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  getFeaturedSourceAvailability,
  isFeaturedForwardCapsule,
} from "../utils/timeCapsuleForward";

const IDLE_STATE = { status: "idle", sourceCapsule: null };

/**
 * Resolves the original featured capsule referenced by a forwarded envelope.
 * Self-gates: does nothing unless the capsule is a claimed/full featured
 * forward. Any failure resolves to "unavailable" — never an error state
 * (FR-A7): the recipient sees a friendly fallback instead.
 */
export default function useForwardedFeaturedSource(capsule) {
  const [state, setState] = useState(IDLE_STATE);

  const sourceCapsuleId = String(capsule?.sourceCapsuleId || "").trim();
  const shouldResolve =
    isFeaturedForwardCapsule(capsule) && capsule?.previewLocked !== true;

  useEffect(() => {
    if (!shouldResolve || !sourceCapsuleId) {
      setState(IDLE_STATE);
      return undefined;
    }

    let active = true;
    setState({ status: "loading", sourceCapsule: null });

    (async () => {
      try {
        const snap = await getDoc(doc(db, "timeCapsules", sourceCapsuleId));
        if (!active) return;
        const sourceCapsule = snap.exists()
          ? { id: snap.id, ...(snap.data() || {}) }
          : null;
        if (
          sourceCapsule &&
          getFeaturedSourceAvailability(sourceCapsule) === "available"
        ) {
          setState({ status: "available", sourceCapsule });
        } else {
          setState({ status: "unavailable", sourceCapsule: null });
        }
      } catch {
        if (!active) return;
        setState({ status: "unavailable", sourceCapsule: null });
      }
    })();

    return () => {
      active = false;
    };
  }, [shouldResolve, sourceCapsuleId]);

  return state;
}
